import { RudderAnalytics } from "@rudderstack/analytics-js"

import {
  AnalyticsPlugin,
  Event,
  Parameter,
  User,
} from "@treefort/lib/analytics"

import config from "../../../../config"
import { client } from "../../client"
import {
  logRudderStackError,
  transformEvents,
  transformParameters,
} from "./utils"

const rudder = new RudderAnalytics()
const customContext = transformParameters(client)

class RudderStackAnalyticsPlugin implements AnalyticsPlugin {
  private initialized: Promise<unknown>

  constructor() {
    this.initialized = new Promise((resolve, reject) => {
      try {
        rudder.load(
          config.RUDDERSTACK_WRITE_KEY,
          config.RUDDERSTACK_DATA_PLANE_URL,
          {
            configUrl: config.RUDDERSTACK_CONTROL_PLANE_URL,
            pluginsSDKBaseURL: config.RUDDERSTACK_PLUGINS_SDK_BASE_URL,
            storage: { type: "localStorage" },
            onLoaded: resolve,
          },
        )
      } catch (cause) {
        reject(cause)
      }
    }).catch(logRudderStackError("Failed to initialze the SDK"))
  }

  identifyUser = async (user: User) => {
    await this.initialized
    const userId = user[Parameter.UserId] ?? undefined
    if (!userId && rudder.getUserTraits()?.[Parameter.UserAuthenticated]) {
      rudder.reset(false)
    }

    const userParameters = transformParameters(user)
    rudder.identify(userId || "", userParameters, customContext)
  }

  getAnonymousId = async () => {
    await this.initialized
    const anonymousId = rudder.getAnonymousId()
    if (!anonymousId) {
      logRudderStackError("Failed to obtain an anonymous ID")
    }
    return anonymousId
  }

  logEvent = async (event: Event): Promise<void> => {
    await this.initialized
    await Promise.all(
      transformEvents([event]).map((event) => {
        switch (event.type) {
          case "screen":
            return rudder.page(event.title, event.parameters, customContext)
          case "track":
            return rudder.track(event.name, event.parameters, customContext)
        }
      }),
    )
  }
}

export const rudderstackAnalyticsPlugin = new RudderStackAnalyticsPlugin()
